import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2, AlertTriangle, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ password: "", confirmPassword: "" });

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const res = await axios.get(`${API}/auth/reset-password/${token}`);
      setEmail(res.data.email);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired reset link");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (form.password !== form.confirmPassword) { toast.error("Passwords do not match"); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password: form.password });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 size={32} className="animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white border border-slate-200 rounded-sm p-8 shadow-sm">
            <AlertTriangle size={48} className="mx-auto text-amber-500 mb-4" />
            <h1 className="text-xl font-semibold text-slate-900 mb-2">Reset Link Invalid</h1>
            <p className="text-slate-500 text-sm mb-6">{error}</p>
            <Button onClick={() => navigate("/login")} className="rounded-sm bg-[#0c1829] hover:bg-[#152238]">Back to Login</Button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white border border-slate-200 rounded-sm p-8 shadow-sm">
            <CheckCircle size={48} className="mx-auto text-emerald-500 mb-4" />
            <h1 className="text-xl font-semibold text-slate-900 mb-2">Password Reset</h1>
            <p className="text-slate-500 text-sm mb-6">Your password has been updated. You can now sign in with your new password.</p>
            <Button onClick={() => navigate("/login")} className="rounded-sm bg-[#0c1829] hover:bg-[#152238]" data-testid="go-to-login-btn">Sign In</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Con<span className="text-cyan-500">T</span>rack
          </h1>
          <p className="text-slate-500 text-sm mt-1">Contract Lifecycle Management</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-1">Choose a new password</h2>
          <p className="text-sm text-slate-500 mb-5">for <strong>{email}</strong></p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">New Password</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Min 8 chars, upper + lower + number + special"
                className="h-12 rounded-sm"
                data-testid="reset-password-input"
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">Confirm Password</Label>
              <Input
                type="password"
                value={form.confirmPassword}
                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                placeholder="Re-enter your password"
                className="h-12 rounded-sm"
                data-testid="reset-confirm-input"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting || !form.password || !form.confirmPassword}
              className="w-full h-12 rounded-sm bg-[#0c1829] hover:bg-[#152238] text-white font-semibold gap-2"
              data-testid="reset-submit-btn"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
              Reset Password
            </Button>
          </form>

          <p className="text-center text-xs text-slate-400 mt-4">
            <a href="/login" className="hover:underline">Back to login</a>
          </p>
        </div>
      </div>
    </div>
  );
}
